import {AppSku} from "crqlar-react-common/dist/models/app/appSku";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {useCanViewInsights} from "crqlar-react-common/dist/utils/hooks/insights/useCanViewInsights";
import {useMarketplaceApps} from "crqlar-react-common/dist/utils/hooks/app/useMarketplaceApps";
import {useOrganizationApps} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {resolvePermittedApp} from "crqlar-react-common/dist/utils/route/permittedApp";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useQuery} from "react-query";
import {Navigate, useLocation, useParams} from "react-router-dom";
import SurveyModuleIcon from "../../common/icons/SurveyModuleIcon";
import type {TabRoute} from "../../layout/PageShell";
import {PageShell} from "../../layout/PageShell";
import SurveysAutomationsTab from "./SurveysAutomationsTab";
import SurveysDailyListTab from "./SurveysDailyListTab";
import SurveysEvaluationTab from "./SurveysEvaluationTab";
import SurveysInsightsTab from "./SurveysInsightsTab";
import SurveysOverviewTab from "./SurveysOverviewTab";
import SurveysSubmissionsTab from "./SurveysSubmissionsTab";

export default function Surveys() {
    const {t} = useLocalize();
    const {appId: routeAppId} = useParams<"appId">();
    const {pathname} = useLocation();
    const organizationApps = useOrganizationApps();
    const marketplaceApps = useMarketplaceApps();
    const {canViewInsights} = useCanViewInsights();

    // Same query as useAllSectors, read here for its loading state: the daily list only lists hotel
    // stays, so an organisation without an accommodation sector has nothing to show in it.
    const sectorsQuery = useQuery('allSectors', () => SectorService.getAllSectors({
        limit: {offset: 0, limit: 1000, sort: [{field: "name", direction: "ASC"}]},
    }));
    const hasStays = sectorsQuery.data
        ? (sectorsQuery.data.content ?? []).some(sector => sector.sectorType === SectorType.ACCOMMODATION)
        : undefined;

    const appId = routeAppId ?? resolvePermittedApp({
        organizationApps,
        marketplaceApps,
        pathname,
        matcher: {sku: AppSku.SURVEYS},
        legacyName: "Surveys",
    })?.id;

    // The module lands on the overview. The daily list needs stays: without any, an old link to it goes there too.
    if (appId && hasStays === false && pathname.startsWith(`/surveys/daily/${appId}`)) {
        return <Navigate to={`/surveys/overview/${appId}`} replace/>;
    }

    const tabs: TabRoute[] = appId
        ? [
            {
                path: `/surveys/overview/${appId}`,
                label: t("Overview"),
                render: () => <SurveysOverviewTab/>,
            },
            // Shown while the sectors load, so the tab does not pop in late for the organisations that have stays.
            ...(hasStays !== false
                ? [{
                    path: `/surveys/daily/${appId}`,
                    label: t("Daily List"),
                    render: () => <SurveysDailyListTab/>,
                }]
                : []),
            // Open to every module user: its counts come from the list, not from Insights.
            {
                path: `/surveys/submissions/${appId}`,
                label: t("Submissions"),
                render: () => <SurveysSubmissionsTab/>,
            },
            {
                path: `/surveys/automations/${appId}`,
                label: t("Automations"),
                render: () => <SurveysAutomationsTab/>,
            },
            // Management-only: the same rule the API enforces on the insights endpoints.
            ...(canViewInsights
                ? [{
                    path: `/surveys/insights/${appId}`,
                    label: t("Insights"),
                    render: () => <SurveysInsightsTab/>,
                }]
                : []),
            // Open to every module user, as it was on the settings survey page it came from.
            {
                path: `/surveys/evaluation/${appId}`,
                label: t("Evaluation"),
                render: () => <SurveysEvaluationTab/>,
            },
        ]
        : [];

    return (
        <PageShell
            icon={<SurveyModuleIcon fontSize="inherit"/>}
            title={t("Surveys")}
            documentTitle={t("Surveys")}
            tabs={tabs}
        />
    );
}
